import { Modal } from "antd";
import { runDiscountCodeAction } from "services/discountCodes.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { CODES_COPY } from "./discountCodes.copy";
import {
  buildRunAgainRun,
  getRunAgainDefaults,
  isSwitchedOffByShoup,
} from "./discountCodes.helpers";
import RunAgainFields from "../_blocks/RunAgainFields";

/** Copies a code and shows the "Code copied" toast. */
export function copyCode(code) {
  navigator.clipboard.writeText(code);
  renderSuccessNotifications({
    title: CODES_COPY.copiedTitle,
    message: CODES_COPY.copiedMessage(code),
  });
}

const ACTION_TOAST = {
  PAUSE: CODES_COPY.toastPaused,
  RESUME: CODES_COPY.toastResumed,
  END: CODES_COPY.toastEnded,
  RUN_AGAIN: CODES_COPY.toastRunAgain,
};

/**
 * Pause / Resume act straight away (reversible); End now and Run again ask
 * first (ui-spec §2.1.4). Run again's confirm also asks for the new run's
 * expiry and optional start. `onDone(detail)` runs after a successful action.
 * Render `contextHolder` once so the confirms get the portal theme.
 */
export default function useCodeActions({ onDone } = {}) {
  const [modal, contextHolder] = Modal.useModal();

  const send = async (row, action, run) => {
    const { data, errors } = await runDiscountCodeAction(
      row.code_id,
      action,
      run
    );
    if (errors?.length) {
      renderErrorNotifications(errors);
      return false;
    }
    renderSuccessNotifications({ title: ACTION_TOAST[action](row.code) });
    onDone?.(data, action);
    return true;
  };

  const runAction = (row, action) => {
    if (action === "END") {
      modal.confirm({
        title: CODES_COPY.confirmEndTitle,
        icon: null,
        content: CODES_COPY.confirmEndContent(row.code),
        okText: CODES_COPY.confirmEndOk,
        okType: "danger",
        cancelText: CODES_COPY.btnCancel,
        maskClosable: true,
        onOk: () => send(row, action),
      });
      return;
    }
    if (action === "RUN_AGAIN" && isSwitchedOffByShoup(row)) {
      // Only ShoUp can bring back a code it switched off (D76).
      renderErrorNotifications([{ message: CODES_COPY.switchedOffDetail }]);
      return;
    }
    if (action === "RUN_AGAIN") {
      let form = null;
      modal.confirm({
        title: CODES_COPY.confirmRunAgainTitle(row.code),
        icon: null,
        content: (
          <>
            {CODES_COPY.confirmRunAgainContent}
            <RunAgainFields
              initialValues={getRunAgainDefaults(row)}
              onReady={(f) => {
                form = f;
              }}
            />
          </>
        ),
        okText: CODES_COPY.confirmRunAgainOk,
        cancelText: CODES_COPY.btnCancel,
        maskClosable: true,
        // A rejected promise keeps the confirm open (bad dates or a failed call).
        onOk: async () => {
          const values = await form.validateFields();
          const done = await send(row, action, buildRunAgainRun(values));
          if (!done) throw new Error("RUN_AGAIN failed");
        },
      });
      return;
    }
    return send(row, action);
  };

  return { runAction, contextHolder };
}
